import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { useState } from "react";
import { useLocalState } from "../../utils/hooks/global/useLocalStorage";
import CustomButton from "./CustomButton";

// Pauses or resumes an exchange's status check, after a confirmation. A
// paused check does not run, alert or change the status until resumed.
// onChange receives the updated check health from the backend.
const PauseCheckButton = ({ slug, name, paused, onChange, color }) => {
  const [jwt] = useLocalState(null, "jwt");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleConfirm = async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/exchanges/${slug}/checks/`,
        {
          method: "PATCH",
          credentials: "include",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: "Bearer " + jwt,
          },
          body: JSON.stringify({ paused: !paused }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || `Request failed (${response.status})`);
      }
      setOpen(false);
      onChange?.(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <CustomButton
        buttonText={paused ? "Resume check" : "Pause check"}
        icon={paused ? PlayArrowRoundedIcon : PauseRoundedIcon}
        color={color ?? "neutralOutlined"}
        onClick={() => {
          setError(null);
          setOpen(true);
        }}
      />
      <Dialog
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow:
              "rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px",
            width: "440px",
            maxWidth: "90%",
          },
        }}
        open={open}
        onClose={() => !saving && setOpen(false)}
      >
        <DialogTitle>
          {paused ? "Resume" : "Pause"} the {name} status check?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {paused
              ? "The check runs again from the next cycle and updates the deposit/withdrawal status within a minute."
              : "The check stops running and alerting until someone resumes it. Its deposit/withdrawal status shows as paused instead of online or offline. A message is posted to the status Slack channel."}
          </DialogContentText>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <CustomButton
            onClick={() => setOpen(false)}
            buttonText="Cancel"
            disabled={saving}
          />
          <CustomButton
            onClick={handleConfirm}
            buttonText={saving ? "Saving…" : paused ? "Resume" : "Pause"}
            color="black"
            disabled={saving}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PauseCheckButton;
